<template>
  <div id="app">
    <div class="app-container">
      <!-- 顶部导航栏 -->
      <header class="top-navbar">
        <div class="navbar-header">
          <div class="navbar-logo-wrap">
            <img src="@/assets/logo.png" alt="格瑞创新 Logo" class="navbar-logo" />
          </div>
          <h2>格瑞<b>创新</b></h2>
        </div>
        <nav class="navbar-nav">
          <div class="nav-scroll">
            <div
              v-for="item in navItems"
              :key="item.route"
              class="nav-item"
              :class="{ active: currentRoute === item.route }"
              @click="handleNav(item.key)"
            >
              <span class="nav-icon" v-html="item.icon"></span>
              <span class="nav-text">{{ item.text }}</span>
            </div>
          </div>
        </nav>
        <div class="navbar-actions">
          <button v-if="!isLoggedIn" class="login-btn" @click="handleNav('profile')">登录 / 注册</button>
          <div v-else class="user-menu" :class="{ open: dropdownOpen }" ref="userMenu">
            <div class="user-menu-trigger" @click="toggleDropdown">
              <div class="user-badge">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="8" r="4"/><path d="M4 21c0-4 4-6 8-6s8 2 8 6"/></svg>
              </div>
              <span class="user-name">{{ username || '用户' }}</span>
              <svg class="dropdown-chevron" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="6 9 12 15 18 9"/></svg>
              <span v-if="unreadCount > 0" class="user-notice-dot"></span>
            </div>
            <div class="user-menu-dropdown">
              <div class="dropdown-header">
                <div class="user-badge lg">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="8" r="4"/><path d="M4 21c0-4 4-6 8-6s8 2 8 6"/></svg>
                </div>
                <div class="dropdown-userinfo">
                  <span class="dropdown-username">{{ username || '用户' }}</span>
                  <span v-if="vipInfo.isVip" class="vip-tag">VIP</span>
                  <span v-else class="user-level">普通用户</span>
                </div>
              </div>
              <div class="dropdown-divider"></div>
              <div class="dropdown-item" @click="navFromDropdown('profile')">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="8" r="4"/><path d="M4 21c0-4 4-6 8-6s8 2 8 6"/></svg>
                <span>个人中心</span>
              </div>
              <div class="dropdown-item" @click="navFromDropdown('checkin')">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="4" width="18" height="18" rx="2"/><path d="M16 2v4M8 2v4M3 10h18"/><path d="m9 16 2 2 4-4"/></svg>
                <span>每日签到</span>
              </div>
              <div class="dropdown-item" @click="navFromDropdown('notifications')">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M18 8a6 6 0 0 0-12 0c0 7-3 9-3 9h18s-3-2-3-9"/><path d="M13.73 21a2 2 0 0 1-3.46 0"/></svg>
                <span>消息通知</span>
                <span v-if="unreadCount > 0" class="dropdown-badge">{{ unreadCount > 99 ? '99+' : unreadCount }}</span>
              </div>
              <div class="dropdown-divider"></div>
              <div class="dropdown-item" @click="navFromDropdown('youth-mode')">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>
                <span>青少年模式</span>
              </div>
              <div class="dropdown-item" @click="navFromDropdown('help')">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="9"/><path d="M12 16v-4M12 8h.01"/></svg>
                <span>帮助中心</span>
              </div>
              <div class="dropdown-divider"></div>
              <div class="dropdown-item danger" @click="logout">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/><polyline points="16 17 21 12 16 7"/><line x1="21" y1="12" x2="9" y2="12"/></svg>
                <span>退出登录</span>
              </div>
            </div>
          </div>
        </div>
      </header>

      <!-- 主内容区域 -->
      <main class="main-content" :class="{ 'reader-mode': isReaderPage }">
        <div class="content-area">
          <router-view />
        </div>
      </main>
    </div>
    <FooterBar v-if="!isReaderPage" />
    <YouthModeOverlay />
    <YouthModePrompt />
  </div>
</template>

<script>
import FooterBar from './components/FooterBar.vue';
import YouthModeOverlay from './components/YouthModeOverlay.vue';
import YouthModePrompt from './components/YouthModePrompt.vue';
import { getUserVipInfo, getUnreadCount } from './api/index';

export default {
  name: 'App',
  data() {
    return {
      isLoggedIn: false,
      username: '',
      unreadCount: 0,
      dropdownOpen: false,
      vipInfo: {
        isVip: false,
        packageName: ''
      },
      navItems: [
        { key: 'home', route: '/', icon: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M3 11l9-8 9 8"/><path d="M5 10v10h14V10"/></svg>', text: '首页' },
        { key: 'category', route: '/category', icon: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/><rect x="3" y="14" width="7" height="7"/><rect x="14" y="14" width="7" height="7"/></svg>', text: '频道' },
        { key: 'ranking', route: '/ranking', icon: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M3 17l6-6 4 4 8-8"/><path d="M21 7v6h-6"/></svg>', text: '排行榜' },
        { key: 'update', route: '/update', icon: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="9"/><path d="M12 8v4l3 2"/></svg>', text: '今日更新' },
        { key: 'submission', route: '/submission', icon: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M12 20h9"/><path d="M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4z"/></svg>', text: '投稿' },
        { key: 'vip', route: '/vip', icon: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M3 7l4 5 5-7 5 7 4-5v11H3z"/></svg>', text: '开通VIP' },
        { key: 'coins', route: '/coins', icon: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="9"/><path d="M12 7v10M9.5 9.5h3.5a1.5 1.5 0 0 1 0 3h-2a1.5 1.5 0 0 0 0 3H14"/></svg>', text: '金币充值' },
        { key: 'about', route: '/about', icon: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="9"/><path d="M12 16v-4M12 8h.01"/></svg>', text: '关于我们' }
      ]
    }
  },
  computed: {
    currentRoute() {
      return this.$route.path;
    },
    isReaderPage() {
      return this.$route.path.startsWith('/reader');
    }
  },
  components: {
    FooterBar,
    YouthModeOverlay,
    YouthModePrompt
  },
  mounted() {
    this.checkLoginStatus();
    document.addEventListener('click', this.onDocumentClick);
    // 监听 Profile 等子页面登录/登出后的全局通知，刷新顶部导航状态
    window.addEventListener('app:auth-changed', this.onAuthChanged);
  },
  beforeUnmount() {
    document.removeEventListener('click', this.onDocumentClick);
    window.removeEventListener('app:auth-changed', this.onAuthChanged);
  },
  watch: {
    '$route'() {
      this.checkLoginStatus();
    }
  },
  methods: {
    handleNav(page) {
      const routeMap = {
        home: '/', category: '/category', ranking: '/ranking', update: '/update',
        checkin: '/checkin', submission: '/submission', profile: '/profile',
        vip: '/vip', coins: '/coins', about: '/about', notifications: '/notifications',
        help: '/help', 'youth-mode': '/youth-mode'
      };
      const target = routeMap[page];
      if (target && this.$route.path !== target) {
        this.$router.push(target);
      }
    },

    toggleDropdown() {
      this.dropdownOpen = !this.dropdownOpen;
    },

    closeDropdown() {
      this.dropdownOpen = false;
    },

    onDocumentClick(e) {
      const menu = this.$refs.userMenu;
      if (menu && !menu.contains(e.target)) {
        this.dropdownOpen = false;
      }
    },

    navFromDropdown(page) {
      this.dropdownOpen = false;
      this.handleNav(page);
    },

    logout() {
      this.dropdownOpen = false;
      localStorage.removeItem('userToken');
      localStorage.removeItem('username');
      this.isLoggedIn = false;
      this.username = '';
      this.unreadCount = 0;
      if (this.$route.path !== '/') {
        this.$router.push('/');
      }
    },
    
    checkLoginStatus() {
      const token = localStorage.getItem('userToken');
      const username = localStorage.getItem('username');
      this.isLoggedIn = !!token;
      this.username = username || '';
      
      if (token) {
        this.fetchVipInfo();
        this.fetchUnreadCount();
      } else {
        this.unreadCount = 0;
      }
    },
    
    onAuthChanged() {
      this.checkLoginStatus();
    },

    async fetchUnreadCount() {
      try {
        const res = await getUnreadCount();
        if (res.data && res.data.code === 1) {
          this.unreadCount = res.data.data.count || 0;
        }
      } catch (e) {
        // 通知接口未就绪时静默降级，不打断主流程
        this.unreadCount = 0;
      }
    },
    
    async fetchVipInfo() {
      try {
        const token = localStorage.getItem('userToken');
        if (!token) return;
        
        const res = await getUserVipInfo();
        if (res.data && res.data.code === 1) {
          const data = res.data.data;
          this.vipInfo.isVip = data.isVip;
          
          if (data.isVip && data.vipInfo) {
            this.vipInfo.packageName = data.vipInfo.package_name;
          }
        }
      } catch (e) {
        console.error('获取VIP信息失败', e);
      }
    },
    
    getPageTitle() {
      const routeMap = {
        '/': '首页',
        '/category': '频道',
        '/ranking': '排行榜',
        '/update': '今日更新',
        '/submission': '投稿',
        '/profile': '个人中心',
        '/vip': '开通VIP',
        '/coins': '金币充值',
        '/help': '帮助中心',
        '/about': '关于我们'
      };
      return routeMap[this.currentRoute] || '格瑞创新';
    }
  }
}
</script>

<style>
* {
  margin: 0;
  padding: 0;
  box-sizing: border-box;
}

body {
  font-family: var(--sans);
  background: var(--bg);
  color: var(--text);
  overflow-x: hidden;
}

html {
  overflow-x: hidden;
}

#app {
  width: 100vw;
  min-height: 100vh;
}

/* 应用容器 */
.app-container {
  display: flex;
  flex-direction: column;
  width: 100%;
  min-height: 100vh;
}

/* 顶部导航栏 - 原站深蓝渐变，保留精致布局 */
.top-navbar {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: var(--nav-h);
  background: linear-gradient(135deg, #0c1929 0%, #1a2f4a 45%, #1e4976 100%);
  color: #e8f4fc;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 24px;
  border-bottom: 1px solid rgba(96, 165, 250, 0.15);
  box-shadow: 0 2px 12px rgba(12, 25, 41, 0.35);
  z-index: 1000;
}

.navbar-header {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-shrink: 0;
}

.navbar-logo-wrap {
  flex-shrink: 0;
  padding: 5px 8px;
  background: linear-gradient(180deg, #ffffff 0%, #eef6ff 100%);
  border-radius: 10px;
  line-height: 0;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
}

.navbar-logo {
  width: 40px;
  height: auto;
  display: block;
}

.navbar-header h2 {
  font-size: 19px;
  font-weight: 700;
  margin: 0;
  color: #dbeafe;
  letter-spacing: 0.2px;
}

.navbar-header h2 b {
  color: #4facfe;
  font-weight: 700;
}

.navbar-nav {
  flex: 1;
  display: flex;
  justify-content: center;
  overflow-x: auto;
  overflow-y: hidden;
  margin: 0 16px;
  -ms-overflow-style: none;
  scrollbar-width: none;
}

.navbar-nav::-webkit-scrollbar {
  display: none;
}

/* 固定滚动导航容器 */
.nav-scroll {
  display: flex;
  gap: 2px;
  height: 44px;
  align-items: center;
}

.nav-item {
  display: flex;
  align-items: center;
  gap: 7px;
  height: 40px;
  cursor: pointer;
  transition: background var(--t-fast), color var(--t-fast);
  border-radius: 9px;
  font-size: 13px;
  white-space: nowrap;
  color: rgba(232, 244, 252, 0.78);
  flex-shrink: 0;
  padding: 0 13px;
}

.nav-item:hover {
  background: rgba(255, 255, 255, 0.10);
  color: #fff;
}

.nav-item.active {
  background: rgba(56, 189, 248, 0.22);
  color: #7dd3fc;
}

.nav-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.nav-icon svg {
  width: 17px;
  height: 17px;
  display: block;
  stroke: rgba(232, 244, 252, 0.9);
  fill: none;
}

.nav-item:hover .nav-icon svg {
  stroke: #fff;
}

.nav-item.active .nav-icon svg {
  stroke: #7dd3fc;
}

.nav-text {
  font-weight: 500;
}

.nav-item.active .nav-text {
  font-weight: 600;
}

/* 右侧用户操作区 */
.navbar-actions {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  gap: 14px;
}

/* 登录按钮 */
.login-btn {
  padding: 9px 22px;
  background: linear-gradient(135deg, #4facfe 0%, #00f2fe 100%);
  color: #fff;
  border: none;
  border-radius: 20px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: transform var(--t-fast), box-shadow var(--t-fast);
  box-shadow: 0 4px 12px rgba(79, 172, 254, 0.35);
}

.login-btn:hover {
  transform: translateY(-1px);
  box-shadow: 0 6px 16px rgba(79, 172, 254, 0.5);
}

/* 用户头像下拉菜单 */
.user-menu {
  position: relative;
  flex-shrink: 0;
}

.user-menu-trigger {
  display: flex;
  align-items: center;
  gap: 10px;
  height: 44px;
  padding: 0 10px 0 6px;
  border-radius: 22px;
  background: rgba(255, 255, 255, 0.08);
  border: 1px solid rgba(255, 255, 255, 0.14);
  cursor: pointer;
  color: #e8f4fc;
  transition: background var(--t-fast), border-color var(--t-fast), box-shadow var(--t-fast);
  user-select: none;
}

.user-menu-trigger:hover,
.user-menu.open .user-menu-trigger {
  background: rgba(255, 255, 255, 0.16);
  border-color: rgba(79, 172, 254, 0.55);
  box-shadow: 0 4px 14px rgba(0, 0, 0, 0.18);
}

.user-badge {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: linear-gradient(135deg, #4facfe 0%, #00f2fe 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  flex-shrink: 0;
}

.user-badge svg {
  width: 18px;
  height: 18px;
}

.user-badge.lg {
  width: 48px;
  height: 48px;
}

.user-badge.lg svg {
  width: 26px;
  height: 26px;
}

.user-name {
  font-size: 14px;
  font-weight: 500;
  max-width: 90px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.dropdown-chevron {
  width: 14px;
  height: 14px;
  color: rgba(255, 255, 255, 0.65);
  transition: transform var(--t-fast);
  flex-shrink: 0;
}

.user-menu.open .dropdown-chevron {
  transform: rotate(180deg);
}

.user-notice-dot {
  position: absolute;
  top: 6px;
  right: 6px;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: var(--danger);
  border: 2px solid #1a2f4a;
}

/* 下拉面板 */
.user-menu-dropdown {
  position: absolute;
  top: calc(100% + 10px);
  right: 0;
  width: 220px;
  background: linear-gradient(180deg, #1a2f4a 0%, #0f1c2e 100%);
  border: 1px solid rgba(96, 165, 250, 0.22);
  border-radius: 14px;
  padding: 8px;
  box-shadow: 0 16px 50px rgba(0, 0, 0, 0.45);
  opacity: 0;
  visibility: hidden;
  transform: translateY(-8px) scale(0.98);
  transform-origin: top right;
  transition: opacity var(--t-fast), transform var(--t-fast), visibility var(--t-fast);
  z-index: 1100;
}

.user-menu.open .user-menu-dropdown {
  opacity: 1;
  visibility: visible;
  transform: translateY(0) scale(1);
}

.user-menu-dropdown::before {
  content: '';
  position: absolute;
  top: -6px;
  right: 22px;
  width: 12px;
  height: 12px;
  background: #1a2f4a;
  border-left: 1px solid rgba(96, 165, 250, 0.22);
  border-top: 1px solid rgba(96, 165, 250, 0.22);
  transform: rotate(45deg);
}

.dropdown-header {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 10px;
}

.dropdown-userinfo {
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
}

.dropdown-username {
  font-size: 15px;
  font-weight: 600;
  color: #fff;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.vip-tag {
  align-self: flex-start;
  font-size: 11px;
  font-weight: 700;
  color: #7c2d12;
  background: linear-gradient(135deg, #fcd34d 0%, #fbbf24 100%);
  padding: 2px 8px;
  border-radius: 99px;
}

.user-level {
  font-size: 12px;
  color: rgba(255, 255, 255, 0.6);
}

.dropdown-divider {
  height: 1px;
  background: rgba(255, 255, 255, 0.1);
  margin: 6px 4px;
}

.dropdown-item {
  display: flex;
  align-items: center;
  gap: 11px;
  padding: 10px 12px;
  border-radius: 10px;
  color: rgba(255, 255, 255, 0.88);
  font-size: 14px;
  cursor: pointer;
  transition: background var(--t-fast), color var(--t-fast);
}

.dropdown-item svg {
  width: 18px;
  height: 18px;
  color: rgba(255, 255, 255, 0.65);
  flex-shrink: 0;
  transition: color var(--t-fast);
}

.dropdown-item:hover {
  background: rgba(79, 172, 254, 0.14);
  color: #fff;
}

.dropdown-item:hover svg {
  color: var(--brand);
}

.dropdown-item.danger {
  color: rgba(255, 255, 255, 0.75);
}

.dropdown-item.danger:hover {
  background: rgba(248, 113, 113, 0.12);
  color: #fecaca;
}

.dropdown-item.danger:hover svg {
  color: #f87171;
}

.dropdown-badge {
  margin-left: auto;
  min-width: 18px;
  height: 18px;
  padding: 0 6px;
  border-radius: 99px;
  background: var(--danger);
  color: #fff;
  font-size: 11px;
  font-weight: 700;
  line-height: 18px;
  text-align: center;
}

/* 主内容区域 */
.main-content {
  flex: 1;
  display: flex;
  flex-direction: column;
  background: var(--bg);
  min-width: 0;
  max-width: 100%;
  margin-top: var(--nav-h);
}

/* 阅读器页面：单页模式截断外层滚动 */
.main-content.reader-mode {
  overflow: hidden;
}

/* 阅读器页面：长图模式放开所有层级，页面自然滚动 */
.main-content.reader-mode.continuous-scroll {
  overflow: visible;
}

.main-content.reader-mode .content-area {
  overflow: visible;
  padding: 0;
  flex: 1;
  display: flex;
  flex-direction: column;
}

/* 内容区域 */
.content-area {
  flex: 1;
  padding: 30px;
  overflow-y: auto;
  background: var(--bg);
  max-width: 100%;
  margin: 0 auto;
  width: 100%;
}

/* 青少年模式提示条激活时，内容区下移 */
body.youth-mode-bar-active .main-content {
  margin-top: calc(var(--nav-h) + 36px);
}

/* 阅读器内的头部也下移，避免被提示条遮挡 */
body.youth-mode-bar-active .reader-header {
  top: 36px !important;
}

/* 长图模式：body 上的 class，确保整个页面链条不截断 */
body.reader-continuous-mode {
  overflow-y: auto;
}

body.reader-continuous-mode .app-container {
  overflow: visible;
  min-height: auto;
}

body.reader-continuous-mode .main-content,
body.reader-continuous-mode .main-content.reader-mode {
  overflow: visible;
}

body.reader-continuous-mode .content-area {
  overflow: visible;
  padding: 0;
}

/* 响应式设计 */
@media (max-width: 768px) {
  .top-navbar {
    padding: 0 12px;
    height: 60px;
  }

  .main-content {
    margin-top: 60px;
  }

  body.youth-mode-bar-active .main-content {
    margin-top: 96px;
  }

  .navbar-header h2 {
    display: none;
  }

  .navbar-logo {
    width: 34px;
  }

  .nav-item {
    height: 36px;
    font-size: 12px;
    padding: 0 10px;
    gap: 5px;
  }

  .nav-item .nav-text {
    display: none;
  }

  .nav-icon svg {
    width: 19px;
    height: 19px;
    stroke: rgba(232, 244, 252, 0.9);
    fill: none;
  }

  .content-area {
    padding: 16px;
  }

  .navbar-actions {
    gap: 8px;
  }

  .login-btn {
    padding: 7px 14px;
    font-size: 13px;
  }

  .user-menu-trigger {
    padding: 0 4px 0 4px;
    height: 40px;
  }

  .user-name,
  .dropdown-chevron {
    display: none;
  }

  .user-badge {
    width: 30px;
    height: 30px;
  }

  .user-menu-dropdown {
    width: 200px;
    right: -6px;
  }

  .user-menu-dropdown::before {
    right: 14px;
  }
}
</style>
